import { enqueueSnackbar } from '../notificationActions';
import { SnackbarMessageVariants } from '../../../helpers/enums';
import { LaneDetailsService } from '../../../interfaces/marketInsightsUiServiceInterfaces';
import ApiService from '../../../services/apiService';
import Endpoints from '../../../services/endpoints';
import { fetchGeographyPolygon } from '../networkInsights/geographyPolygonActions';
import { AppRoles } from '../../../interfaces/authorizationInterfaces';
import { GeographyPolygonRequest } from '../../../interfaces/marketInsightsServiceInterfaces';

export const LANE_DETAILS_REQUEST = 'LANE_DETAILS_REQUEST';
export const LANE_DETAILS_SUCCESS = 'LANE_DETAILS_SUCCESS';
export const LANE_DETAILS_FAILURE = 'LANE_DETAILS_FAILURE';

export const SAVED_LANES_REQUEST = 'SAVED_LANES_REQUEST';
export const SAVED_LANES_SUCCESS = 'SAVED_LANES_SUCCESS';
export const SAVED_LANES_FAILURE = 'SAVED_LANES_FAILURE';

export const ADD_SAVED_LANE_REQUEST = 'ADD_SAVED_LANE_REQUEST';
export const ADD_SAVED_LANE_SUCCESS = 'ADD_SAVED_LANE_SUCCESS';
export const ADD_SAVED_LANE_FAILURE = 'ADD_SAVED_LANE_FAILURE';

export const DELETE_SAVED_LANE_REQUEST = 'DELETE_SAVED_LANE_REQUEST';
export const DELETE_SAVED_LANE_SUCCESS = 'DELETE_SAVED_LANE_SUCCESS';
export const DELETE_SAVED_LANE_FAILURE = 'DELETE_SAVED_LANE_FAILURE';

const requestLaneDetails = (): object => {
    return {
        type: LANE_DETAILS_REQUEST
    };
};

const receiveLaneDetails = (json: object): object => {
    return {
        type: LANE_DETAILS_SUCCESS,
        payload: json
    };
};

const requestLaneDetailsFailed = (): object => {
    return {
        type: LANE_DETAILS_FAILURE
    };
};

export const fetchLaneDetails = (searchQuery: any, roles: AppRoles, months: number = 2): Function => {
    return async (dispatch: any): Promise<void> => {
        dispatch(requestLaneDetails());

        const requestBody = [{
            ...searchQuery,
            months
        }];

        try {
            const json = await ApiService.post(Endpoints.laneDetails, requestBody);

            if (Array.isArray(json.data) && json.data[0].length > 0) {
                const jsonData = json.data[0];
                // take the last object in the return array as that is the most recent data
                const current: LaneDetailsService = jsonData[jsonData.length - 1];
                dispatch(receiveLaneDetails(current));

                // Once we have the lane data and the user has access to the network insights api, we can then generate the origin/destination polygons.
                if (roles.networkInsights === true) {
                    const inputBody: GeographyPolygonRequest = {
                        origin_area: current.origin_area_id,
                        destination_area: current.destination_area_id
                    };
                    dispatch(fetchGeographyPolygon(inputBody));
                }
            } else {
                // If no lane details are returned dispatch it as error with a given reason.
                dispatch(requestLaneDetailsFailed());
                dispatch(enqueueSnackbar({
                    message: 'Not enough loads to provide lane details for current month.',
                    variant: SnackbarMessageVariants.Info
                }));
            }
        } catch (err) {
            dispatch(requestLaneDetailsFailed());
            dispatch(enqueueSnackbar({
                message: 'Error occurred while fetching lane details for current month.',
                variant: SnackbarMessageVariants.Error
            }));
        }
    };
};

const requestSavedLanes = (): object => {
    return {
        type: SAVED_LANES_REQUEST
    };
};

const receiveSavedLanes = (json: object): object => {
    return {
        type: SAVED_LANES_SUCCESS,
        payload: json
    };
};

const requestSavedLanesFailed = (): object => {
    return {
        type: SAVED_LANES_FAILURE
    };
};

export const fetchSavedLanes = (): Function => {
    return async (dispatch: any): Promise<void> => {
        dispatch(requestSavedLanes());

        try {
            const json = await ApiService.get(Endpoints.allSavedLanes, true);

            if (Object.keys(json).length > 0) {
                dispatch(receiveSavedLanes(json));
            } else {
                dispatch(receiveSavedLanes([]));
            }
        } catch (err) {
            dispatch(requestSavedLanesFailed());
            dispatch(enqueueSnackbar({
                message: 'Error occurred while fetching saved lanes.',
                variant: SnackbarMessageVariants.Error
            }));
        }
    };
};

const requestAddSavedLane = (): object => {
    return {
        type: ADD_SAVED_LANE_REQUEST
    };
};

const receiveAddSavedLane = (): object => {
    return {
        type: ADD_SAVED_LANE_SUCCESS
    };
};

const requestAddSavedLaneFailed = (): object => {
    return {
        type: ADD_SAVED_LANE_FAILURE
    };
};

export const addSavedLane = (savedLane: any): Function => {
    return async (dispatch: any): Promise<void> => {
        dispatch(requestAddSavedLane());

        try {
            const json = await ApiService.post(Endpoints.saveSearchedLane, savedLane, true);

            if (Object.keys(json).length > 0) {
                dispatch(fetchSavedLanes());
                dispatch(receiveAddSavedLane());
                dispatch(enqueueSnackbar({
                    message: 'Lane saved.',
                    variant: SnackbarMessageVariants.Success
                }));
            } else {
                dispatch(receiveAddSavedLane());
            }
        } catch (err) {
            dispatch(requestAddSavedLaneFailed());
            dispatch(enqueueSnackbar({
                message: 'Error occurred while adding saved lane.',
                variant: SnackbarMessageVariants.Error
            }));
        }
    };
};

const requestDeleteSavedLane = (): object => {
    return {
        type: DELETE_SAVED_LANE_REQUEST
    };
};

const receiveDeleteSavedLane = (laneName: string): object => {
    return {
        type: DELETE_SAVED_LANE_SUCCESS,
        payload: laneName
    };
};

const requestDeleteSavedLaneFailed = (): object => {
    return {
        type: DELETE_SAVED_LANE_FAILURE
    };
};

export const deleteSavedLane = (laneName: string): Function => {
    return async (dispatch: any): Promise<void> => {
        dispatch(requestDeleteSavedLane());

        try {
            await ApiService.delete(`${Endpoints.deleteSavedLane}${laneName}/`, true);
            dispatch(receiveDeleteSavedLane(laneName));
            dispatch(enqueueSnackbar({
                message: 'Lane unsaved.',
                variant: SnackbarMessageVariants.Success
            }));

        } catch (err) {
            dispatch(requestDeleteSavedLaneFailed());
            dispatch(enqueueSnackbar({
                message: 'Error occurred while deleting saved lane.',
                variant: SnackbarMessageVariants.Error
            }));
        }
    };
};
